/**
 * Numérotation officielle des titres et documents d'État.
 *
 * Format : `TYPE-AAAA-GN-NNNNN-C` (spécification de sécurité des documents
 * officiels v1.0 §2.1 et §4.1). Le caractère final `C` est un caractère de
 * contrôle : il permet à un inspecteur de rejeter un numéro inventé sans accès
 * à la base, et à la PWA Verify de filtrer les saisies fautives hors ligne.
 *
 * ## Algorithme du caractère de contrôle — « Luhn base 36, modulo 26 »
 *
 * La spécification prescrit un « algorithme de Luhn adapté modulo 26 sur
 * lettres » sans en donner le détail. La transposition retenue ici est la
 * généralisation directe de Luhn à un alphabet de 36 symboles :
 *
 * 1. Corps du numéro = `TYPE + AAAA + GN + NNNNN`, tirets retirés (14 caractères).
 *    Le type et l'année entrent dans le calcul : substituer `PRM` à `LIC` sur un
 *    document falsifié invalide donc le caractère de contrôle.
 * 2. Valeur d'un symbole : `0`–`9` → 0 à 9, `A`–`Z` → 10 à 35.
 * 3. Parcours de droite à gauche, positions numérotées à partir de 1 ; les
 *    positions impaires sont doublées.
 * 4. Réduction du doublement en base 36 : si `2v ≥ 36`, on somme les deux
 *    chiffres base 36, soit `2v − 35` (transposition exacte du `2v − 9` de Luhn
 *    en base 10).
 * 5. `C` = lettre d'indice `(26 − (somme mod 26)) mod 26` dans `A`…`Z`.
 *
 * Un numéro tiré au hasard a donc 1 chance sur 26 de passer le contrôle.
 */
import { ALPHABET_CONTROLE, INDICATIF_PAYS, LONGUEUR_SEQUENCE, SEQUENCE_MAX, SEQUENCE_MIN, TYPES_NUMERO_OFFICIEL, } from '@sygap/shared';
import { CODE_ERREUR, ErreurNumerotation } from '../erreurs.js';
/** Année minimale exprimable sur 4 chiffres significatifs. */
export const ANNEE_MIN = 1000;
/** Année maximale exprimable sur 4 chiffres. */
export const ANNEE_MAX = 9999;
/** Gabarit strict du numéro officiel, une fois normalisé. */
const GABARIT_NUMERO = /^[A-Z]{3}-\d{4}-[A-Z]{2}-\d{5}-[A-Z]$/;
/** Symboles admis dans le corps servant au calcul du contrôle. */
const CORPS_ADMIS = /^[0-9A-Z]+$/;
/** Motifs de rejet, du plus structurel au plus fin. */
export const MOTIF_REJET_NUMERO = {
    /** Ne respecte pas le gabarit `TYPE-AAAA-GN-NNNNN-C`. */
    FORMAT: 'FORMAT',
    /** Préfixe de type hors référentiel. */
    TYPE_INCONNU: 'TYPE_INCONNU',
    /** Indicatif pays différent de `GN`. */
    INDICATIF_PAYS: 'INDICATIF_PAYS',
    /** Année hors bornes. */
    ANNEE_INVALIDE: 'ANNEE_INVALIDE',
    /** Séquence hors bornes (`00000`). */
    SEQUENCE_INVALIDE: 'SEQUENCE_INVALIDE',
    /** Caractère de contrôle non concordant : numéro inventé ou mal recopié. */
    CONTROLE_INVALIDE: 'CONTROLE_INVALIDE',
};
// -----------------------------------------------------------------------------
// Caractère de contrôle
// -----------------------------------------------------------------------------
const CODE_ZERO = 48;
const CODE_NEUF = 57;
const CODE_A = 65;
const BASE = 36;
const MODULO = 26;
/** Valeur base 36 d'un symbole déjà validé par `CORPS_ADMIS`. */
function valeurSymbole(codeUnite) {
    return codeUnite >= CODE_ZERO && codeUnite <= CODE_NEUF
        ? codeUnite - CODE_ZERO
        : codeUnite - CODE_A + 10;
}
/** Doublement de Luhn transposé en base 36 : somme des chiffres si dépassement. */
function doubler(valeur) {
    const double = valeur * 2;
    return double >= BASE ? double - (BASE - 1) : double;
}
/**
 * Calcule le caractère de contrôle d'un corps de numéro.
 *
 * @param corps Corps alphanumérique, tirets et espaces tolérés (`LIC2026GN00042`).
 * @returns Une lettre majuscule de `A` à `Z`.
 * @throws {ErreurNumerotation} si le corps est vide ou contient un symbole non admis.
 */
export function calculerCaractereControle(corps) {
    const normalise = corps.replace(/[\s-]/g, '').toUpperCase();
    if (!CORPS_ADMIS.test(normalise)) {
        throw new ErreurNumerotation(CODE_ERREUR.NUMERO_PARAMETRE_INVALIDE, 'Le corps du numéro ne doit contenir que des chiffres et des lettres A-Z.', { corps });
    }
    let somme = 0;
    for (let i = 0; i < normalise.length; i += 1) {
        const positionDepuisDroite = normalise.length - i;
        const valeur = valeurSymbole(normalise.charCodeAt(i));
        somme += positionDepuisDroite % 2 === 1 ? doubler(valeur) : valeur;
    }
    return ALPHABET_CONTROLE.charAt((MODULO - (somme % MODULO)) % MODULO);
}
// -----------------------------------------------------------------------------
// Génération
// -----------------------------------------------------------------------------
function estEntierDansBornes(valeur, min, max) {
    return Number.isInteger(valeur) && valeur >= min && valeur <= max;
}
/**
 * Génère un numéro officiel complet.
 *
 * @throws {ErreurNumerotation} si le type, l'année ou la séquence sont hors référentiel.
 */
export function genererNumeroOfficiel(parametres) {
    const { type, annee, sequence } = parametres;
    if (!TYPES_NUMERO_OFFICIEL.includes(type)) {
        throw new ErreurNumerotation(CODE_ERREUR.NUMERO_PARAMETRE_INVALIDE, `Type de numéro hors référentiel : ${type}.`, { type, typesAdmis: TYPES_NUMERO_OFFICIEL });
    }
    if (!estEntierDansBornes(annee, ANNEE_MIN, ANNEE_MAX)) {
        throw new ErreurNumerotation(CODE_ERREUR.NUMERO_PARAMETRE_INVALIDE, `Année hors bornes : attendu un entier entre ${String(ANNEE_MIN)} et ${String(ANNEE_MAX)}.`, { annee });
    }
    if (!estEntierDansBornes(sequence, SEQUENCE_MIN, SEQUENCE_MAX)) {
        throw new ErreurNumerotation(CODE_ERREUR.NUMERO_PARAMETRE_INVALIDE, `Séquence hors bornes : attendu un entier entre ${String(SEQUENCE_MIN)} et ${String(SEQUENCE_MAX)}.`, { sequence });
    }
    const sequenceFormatee = String(sequence).padStart(LONGUEUR_SEQUENCE, '0');
    const corps = `${type}${String(annee)}${INDICATIF_PAYS}${sequenceFormatee}`;
    const controle = calculerCaractereControle(corps);
    return `${type}-${String(annee)}-${INDICATIF_PAYS}-${sequenceFormatee}-${controle}`;
}
// -----------------------------------------------------------------------------
// Vérification
// -----------------------------------------------------------------------------
function rejet(motif, detail) {
    return { valide: false, motif, detail };
}
/**
 * Vérifie un numéro officiel : gabarit, référentiels et caractère de contrôle.
 *
 * Ne lève jamais d'exception — un numéro invalide est un cas métier courant
 * (saisie manuelle d'un agent, scan dégradé, document falsifié), pas une erreur
 * de programmation.
 *
 * Les espaces et la casse sont normalisés avant contrôle : un agent peut saisir
 * `lic 2026 gn 00042 t`.
 */
export function verifierNumeroOfficiel(numero) {
    const normalise = numero.replace(/\s+/g, '').toUpperCase();
    if (!GABARIT_NUMERO.test(normalise)) {
        return rejet('FORMAT', `Gabarit attendu : TYPE-AAAA-${INDICATIF_PAYS}-NNNNN-C.`);
    }
    const type = normalise.slice(0, 3);
    if (!TYPES_NUMERO_OFFICIEL.includes(type)) {
        return rejet('TYPE_INCONNU', `Préfixe de type hors référentiel : ${type}.`);
    }
    const indicatifPays = normalise.slice(9, 11);
    if (indicatifPays !== INDICATIF_PAYS) {
        return rejet('INDICATIF_PAYS', `Indicatif pays attendu : ${INDICATIF_PAYS}.`);
    }
    const annee = Number(normalise.slice(4, 8));
    if (annee < ANNEE_MIN) {
        return rejet('ANNEE_INVALIDE', `Année antérieure à ${String(ANNEE_MIN)}.`);
    }
    const sequence = Number(normalise.slice(12, 17));
    if (sequence < SEQUENCE_MIN) {
        return rejet('SEQUENCE_INVALIDE', 'La séquence 00000 n’est pas attribuable.');
    }
    const caractereControle = normalise.slice(18);
    const attendu = calculerCaractereControle(normalise.slice(0, 17));
    if (caractereControle !== attendu) {
        return rejet('CONTROLE_INVALIDE', 'Le caractère de contrôle ne correspond pas au numéro.');
    }
    return {
        valide: true,
        analyse: {
            numero: normalise,
            type: type,
            annee,
            indicatifPays,
            sequence,
            caractereControle,
        },
    };
}
/**
 * Décompose un numéro officiel.
 *
 * @throws {ErreurNumerotation} si le numéro est invalide — réserver aux cas où
 * l'appelant a déjà la certitude du format (donnée issue de la base).
 */
export function analyserNumeroOfficiel(numero) {
    const resultat = verifierNumeroOfficiel(numero);
    if (!resultat.valide) {
        throw new ErreurNumerotation(CODE_ERREUR.NUMERO_INVALIDE, `Numéro officiel invalide (${resultat.motif}) : ${resultat.detail}`, { numero, motif: resultat.motif });
    }
    return resultat.analyse;
}
//# sourceMappingURL=numero-officiel.js.map